import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Fallback: send Bearer token if still in sessionStorage (migration period)
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

export const postsAPI = {
  create: (data) => api.post('/posts', data),
  getFeed: (skip = 0, limit = 20) => api.get(`/posts/feed?skip=${skip}&limit=${limit}`),
  getDiscoverFeed: (skip = 0, limit = 20) => api.get(`/posts/feed/discover?skip=${skip}&limit=${limit}`),
  getTrendingFeed: (skip = 0, limit = 20) => api.get(`/posts/feed/trending?skip=${skip}&limit=${limit}`),
  getFollowingFeed: (skip = 0, limit = 20) => api.get(`/posts/following?skip=${skip}&limit=${limit}`),
  getForYouFeed: (skip = 0, limit = 30) => api.get(`/posts/feed/foryou?skip=${skip}&limit=${limit}`),
  getTrending: () => api.get('/posts/trending'),
  getUserPosts: (userId, skip = 0, limit = 20) => api.get(`/posts/user/${userId}?skip=${skip}&limit=${limit}`),
  like: (postId) => api.post(`/posts/${postId}/like`),
  getComments: (postId) => api.get(`/posts/${postId}/comments`),
  addComment: (postId, content) => api.post(`/posts/${postId}/comments`, { content }),
  share: (postId) => api.post(`/posts/${postId}/repost`),
  repost: (postId) => api.post(`/posts/${postId}/repost`),
  isReposted: (postId) => api.get(`/posts/${postId}/is-reposted`),
  pin: (postId) => api.post(`/posts/${postId}/pin`),
  unpin: (postId) => api.delete(`/posts/${postId}/pin`),
  react: (postId, reactionType) => api.post(`/posts/${postId}/react/${reactionType}`),
  getReactions: (postId) => api.get(`/posts/${postId}/reactions`),
  quote: (quotedPostId, content) => api.post('/posts/quote', { quoted_post_id: quotedPostId, content }),
  delete: (postId) => api.delete(`/posts/${postId}`),
  edit: (postId, data) => api.patch(`/posts/${postId}`, data),
};

export const bookmarksAPI = {
  toggle: (postId) => api.post(`/bookmarks/${postId}`),
  getAll: () => api.get('/bookmarks'),
};

export const hashtagsAPI = {
  getTrending: (limit = 10) => api.get(`/hashtags/trending?limit=${limit}`),
  getPosts: (tag, skip = 0, limit = 20) => api.get(`/hashtags/${tag}/posts?skip=${skip}&limit=${limit}`),
  follow: (tag) => api.post(`/hashtags/${tag}/follow`),
  unfollow: (tag) => api.delete(`/hashtags/${tag}/follow`),
  getFollowing: () => api.get('/hashtags/following'),
  isFollowing: (tag) => api.get(`/hashtags/${tag}/is-following`),
};

export const discoverAPI = {
  getSuggestedUsers: () => api.get('/discover/users'),
  getPosts: () => api.get('/discover/posts'),
};

export const communitiesAPI = {
  create: (data) => api.post('/communities', data),
  getAll: (skip = 0, limit = 20) => api.get(`/communities?skip=${skip}&limit=${limit}`),
  join: (communityId) => api.post(`/communities/${communityId}/join`),
};

export const activityAPI = {
  getFeed: () => api.get('/activity'),
};

export const usersAPI = {
  follow: (userId) => api.post(`/users/${userId}/follow`),
  unfollow: (userId) => api.delete(`/users/${userId}/unfollow`),
  getFollowers: (userId, skip = 0, limit = 20) => api.get(`/users/${userId}/followers?skip=${skip}&limit=${limit}`),
  getFollowing: (userId, skip = 0, limit = 20) => api.get(`/users/${userId}/following?skip=${skip}&limit=${limit}`),
  isFollowing: (userId) => api.get(`/users/${userId}/is-following`),
  getSuggested: () => api.get('/users/suggested'),
  updateProfile: (data) => api.put('/users/profile', data),
  getProfile: (userId) => api.get(`/users/${userId}/profile`),
  pinPost: (userId, postId) => api.post(`/users/${userId}/pin/${postId}`),
  search: (query, skip = 0, limit = 20) => api.get(`/search/users?q=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}`),
};

export const searchAPI = {
  posts: (query, skip = 0, limit = 20) => api.get(`/search/posts?q=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}`),
  users: (query, skip = 0, limit = 20) => api.get(`/search/users?q=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}`),
  all: (query, skip = 0, limit = 20) => api.get(`/search/all?q=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}`),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnread: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread/count'),
  dismiss: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear'),
};

export const messagesAPI = {
  send: (recipientId, content) => api.post('/messages', { recipient_id: recipientId, content }),
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (userId) => api.get(`/messages/${userId}`),
  getUnreadCount: () => api.get('/messages/unread/count'),
};

export const storiesAPI = {
  create: (data) => api.post('/stories', data),
  getActive: () => api.get('/stories/active'),
  view: (storyId) => api.post(`/stories/${storyId}/view`),
  delete: (storyId) => api.delete(`/stories/${storyId}`),
  react: (storyId, reaction) => api.post(`/stories/${storyId}/react`, { reaction }),
  getReactions: (storyId) => api.get(`/stories/${storyId}/reactions`),
};

export const pollsAPI = {
  vote: (postId, option) => api.post(`/posts/${postId}/vote`, null, { params: { option } }),
};

export const creatorsAPI = {
  getAnalytics: () => api.get('/creators/analytics'),
  getInsights: () => api.get('/creators/insights'),
};

export const accountsAPI = {
  connect: (data) => api.post('/accounts/connect', data),
  getConnected: () => api.get('/accounts/connected'),
  disconnect: (accountId) => api.delete(`/accounts/${accountId}`),
};

export const uploadAPI = {
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const draftsAPI = {
  getAll: () => api.get('/drafts'),
  save: (data) => api.post('/drafts', data),
  delete: (draftId) => api.delete(`/drafts/${draftId}`),
};

export const scheduledAPI = {
  getAll: () => api.get('/scheduled'),
  create: (data) => api.post('/scheduled', data),
  update: (postId, data) => api.put(`/scheduled/${postId}`, data),
  cancel: (postId) => api.delete(`/scheduled/${postId}`),
};

export const musicAPI = {
  getCatalog: (genre, search) => {
    const params = new URLSearchParams();
    if (genre) params.append('genre', genre);
    if (search) params.append('search', search);
    return api.get(`/music/catalog?${params.toString()}`);
  },
  getTrack: (trackId) => api.get(`/music/track/${trackId}`),
  uploadTrack: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/music/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPostsBySong: (songId) => api.get(`/music/song/${songId}/posts`),
  getGenres: () => api.get('/music/genres'),
  getPopular: () => api.get('/music/popular'),
};

export const arenaAPI = {
  createDebate: (data) => api.post('/arena/debates', data),
  joinDebate: (id) => api.post(`/arena/debates/${id}/join`),
  acceptChallenge: (id) => api.post(`/arena/debates/${id}/accept`),
  declineChallenge: (id) => api.post(`/arena/debates/${id}/decline`),
  submitArgument: (id, data) => api.post(`/arena/debates/${id}/argument`, data),
  vote: (id, side) => api.post(`/arena/debates/${id}/vote`, { side }),
  getActive: (skip = 0, limit = 20) => api.get(`/arena/debates/active?skip=${skip}&limit=${limit}`),
  getOpen: (skip = 0, limit = 20) => api.get(`/arena/debates/open?skip=${skip}&limit=${limit}`),
  getMy: () => api.get('/arena/debates/my'),
  getCompleted: (skip = 0, limit = 20) => api.get(`/arena/debates/completed?skip=${skip}&limit=${limit}`),
  getDebate: (id) => api.get(`/arena/debates/${id}`),
  getLeaderboard: (limit = 20) => api.get(`/arena/leaderboard?limit=${limit}`),
  getMyStats: () => api.get('/arena/my-stats'),
  getPendingCount: () => api.get('/arena/pending-count'),
  spectate: (id) => api.post(`/arena/debates/${id}/spectate`),
  addReaction: (id, emoji) => api.post(`/arena/debates/${id}/react`, { emoji }),
  getReactions: (id, since) => api.get(`/arena/debates/${id}/reactions${since ? `?since=${since}` : ''}`),
  addComment: (id, text) => api.post(`/arena/debates/${id}/comments`, { text }),
  getComments: (id, skip = 0) => api.get(`/arena/debates/${id}/comments?skip=${skip}`),
};

export const quickpointsAPI = {
  create: (data) => api.post('/quickpoints', data),
  getAll: () => api.get('/quickpoints'),
  delete: (id) => api.delete(`/quickpoints/${id}`),
};


export const potdAPI = {
  nominate: (postId) => api.post(`/potd/nominate/${postId}`),
  vote: (postId) => api.post(`/potd/vote/${postId}`),
  getNominations: () => api.get('/potd/nominations/today'),
  getWinnerToday: () => api.get('/potd/winner/today'),
  getWinnerYesterday: () => api.get('/potd/winner/yesterday'),
  checkNominated: (postId) => api.get(`/potd/check-nominated/${postId}`),
  userNominatedToday: () => api.get('/potd/user-nominated-today'),
  selectWinner: () => api.post('/potd/select-winner'),
};

export const securityAPI = {
  getPrivacy: () => api.get('/security/privacy'),
  updatePrivacy: (settings) => api.put('/security/privacy', settings),
  changePassword: (data) => api.post('/security/change-password', data),
  requestReset: (data) => api.post('/security/request-reset', data),
  resetPassword: (data) => api.post('/security/reset-password', data),
  blockUser: (userId) => api.post(`/security/block/${userId}`),
  unblockUser: (userId) => api.delete(`/security/block/${userId}`),
  getBlocked: () => api.get('/security/blocked'),
  report: (data) => api.post('/security/report', data),
  deleteAccount: (data) => api.delete('/security/account', { data }),
  suspendAccount: (data) => api.post('/security/suspend', data),
  reactivateAccount: () => api.post('/security/reactivate'),
  refreshToken: () => api.post('/security/refresh-token'),
  getPreferences: () => api.get('/security/preferences'),
  updatePreferences: (data) => api.put('/security/preferences', data),
};

export const callsAPI = {
  getHistory: () => api.get('/calls/history'),
};

export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getPackages: () => api.get('/wallet/packages'),
  purchase: (packageId, originUrl) => api.post('/wallet/purchase', { package_id: packageId, origin_url: originUrl }),
  checkStatus: (sessionId) => api.get(`/wallet/purchase/status/${sessionId}`),
  getTransactions: () => api.get('/wallet/transactions'),
};

export const tipsAPI = {
  send: (recipientId, amount, targetType, targetId) => api.post('/tips/send', { recipient_id: recipientId, amount, target_type: targetType, target_id: targetId }),
  getLeaderboard: () => api.get('/tips/leaderboard'),
};

export const subscriptionsAPI = {
  setupCreator: (price, tierName, description) => api.post('/creator/setup', { subscription_price: price, tier_name: tierName, description }),
  getMyCreator: () => api.get('/creator/me'),
  getCreator: (userId) => api.get(`/creator/${userId}`),
  subscribe: (creatorId) => api.post('/subscriptions/subscribe', { creator_id: creatorId }),
  unsubscribe: (creatorId) => api.post('/subscriptions/unsubscribe', { creator_id: creatorId }),
  getMy: () => api.get('/subscriptions/my'),
  getSubscribers: () => api.get('/subscriptions/subscribers'),
  check: (creatorId) => api.get(`/subscriptions/check/${creatorId}`),
};

export const liveGiftAPI = {
  sendGift: (streamId, amount, giftType) => api.post('/gifts/live', { stream_id: streamId, amount, gift_type: giftType }),
  getGiftTypes: () => api.get('/gifts/types'),
};

export const ppvAPI = {
  setPPV: (debateId, entryFee) => api.post('/arena/ppv/set', { debate_id: debateId, entry_fee: entryFee }),
  payPPV: (debateId) => api.post('/arena/ppv/pay', null, { params: { debate_id: debateId } }),
  checkAccess: (debateId) => api.get(`/arena/ppv/check/${debateId}`),
};

export const promotedAPI = {
  create: (data) => api.post('/promoted/create', data),
  getFeed: (limit = 2) => api.get(`/promoted/feed?limit=${limit}`),
  recordImpression: (id) => api.post(`/promoted/${id}/impression`),
  recordClick: (id) => api.post(`/promoted/${id}/click`),
  getMy: () => api.get('/promoted/my'),
  sponsorDebate: (data) => api.post('/arena/sponsor', data),
  getDebateSponsor: (debateId) => api.get(`/arena/${debateId}/sponsor`),
};

export default api;
